import { useEffect } from "react";
import type { VoiceAgentService } from "@/services/integrations/elevenlabs/VoiceAgentService";
import { TranscriptStorageService } from "@/services/conversation/TranscriptStorageService";
import { replaceAttachment } from "@/types/voice/genie-attachment";
import type { TranscriptMessage } from "@/pages/agent-interfaces/live-agent/components/TranscriptPanel";

/** The slice of useVoiceAgentConversation's return this hook needs. */
interface VoiceAgentLike {
  getService: () => VoiceAgentService | null;
  getConversationId: () => string | null;
}

interface UseVoiceTranscriptOptions {
  voiceAgent: VoiceAgentLike;
  /** The caller's message-list setter. */
  setMessages: React.Dispatch<React.SetStateAction<TranscriptMessage[]>>;
  /** Typed messages awaiting a matching user-transcript echo. */
  optimisticUserMessagesRef: React.RefObject<string[]>;
  /** Live ref to the conversation's start timestamp, for transcript storage. */
  conversationStartTimeRef: React.RefObject<number | null>;
}

/**
 * Subscribes to the voice service's message stream — plus the links/pictures
 * the Genie puts on screen — and appends each entry to the transcript (and to
 * localStorage, keyed by conversation id). Binds once
 * against the service instance — reading `getService()` at mount is safe
 * because the service object is created before it emits anything, and
 * the refs keep the callback current without re-subscribing.
 */
export const useVoiceTranscript = ({
  voiceAgent,
  setMessages,
  optimisticUserMessagesRef,
  conversationStartTimeRef,
}: UseVoiceTranscriptOptions) => {
  useEffect(() => {
    const service = voiceAgent.getService();
    if (!service) return;
    const append = (newMessage: TranscriptMessage) => {
      setMessages((prev) => [...prev, newMessage]);

      const conversationId = voiceAgent.getConversationId();
      if (conversationId) {
        TranscriptStorageService.addMessage(
          conversationId,
          newMessage,
          conversationStartTimeRef.current || undefined,
        );
      }
    };

    service.onMessage((message, isUser) => {
      if (isUser) {
        const optimisticIndex = optimisticUserMessagesRef.current.indexOf(message);
        if (optimisticIndex !== -1) {
          optimisticUserMessagesRef.current.splice(optimisticIndex, 1);
          return;
        }
      }
      append({ message, isUser, timestamp: new Date() });
    });
    service.onAttachment((attachment, replacesUrl) => {
      if (!replacesUrl) {
        append({ message: "", isUser: false, timestamp: new Date(), attachment });
        return;
      }
      setMessages((prev) => replaceAttachment(prev, replacesUrl, attachment));
      const conversationId = voiceAgent.getConversationId();
      if (conversationId) TranscriptStorageService.replaceAttachment(conversationId, replacesUrl, attachment);
    });
    // setMessages reads live state through refs and setters, so the binding is
    // stable across re-renders; re-subscribing on each change would be churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceAgent]);
};
